import {
  Icon,
  Inline,
  Link,
  Text,
  Tooltip,
} from '@lightspeed/design-system-react'
import { T, useT } from '@transifex/react'
import classNames from 'classnames'
import React from 'react'

import {
  usePosStateManager,
  useSaleManager,
  useUserManager,
  useUserSwitchingService,
} from '../../../ng-compat/hooks'
import { useAppSelector } from '../../../state/hooks'
import { selectCurrentSale } from '../../../state/slices/currentSale/currentSaleSlice'
import { getDisabledTooltipMessage, TooltipContext } from './add-row-tooltips'
import { isDiscountDisabled } from './discount-functions'
import { PromoCode } from './Promotion/PromoCode'

export interface AddRowProps {
  displayDiscountRow: boolean
  setDisplaySaleDiscountRow: (value: boolean) => void
  openDiscountPopover: () => void
  showSaleNoteLink: boolean
  onNoteLinkClick: () => void
}

export const AddRow: React.FC<AddRowProps> = ({
  displayDiscountRow,
  openDiscountPopover,
  setDisplaySaleDiscountRow,
  showSaleNoteLink,
  onNoteLinkClick,
}) => {
  const currentSale = useAppSelector(selectCurrentSale)
  const saleManager = useSaleManager()
  const posStateManager = usePosStateManager()
  const userManager = useUserManager()
  const t = useT()
  const userSwitching = useUserSwitchingService()
  const disabledDiscount = isDiscountDisabled(posStateManager, currentSale)
  const handleDiscountClick = () => {
    if (disabledDiscount) {
      return
    }
    if (userManager.canApplySaleDiscount()) {
      /**
       * The discount link will be removed from the DOM soon after being clicked
       * need setTimeout here so that the Popover.handleDocumentClick method can close any existing opened popover first
       * then the SaleDiscount popover will be opneed
       * Otherwise Popover.handleDocumentClick method exit early and result in two popovers opened at the same time
       */
      setTimeout(() => {
        openDiscountPopover()
        setDisplaySaleDiscountRow(true)
      }, 0)
    } else {
      userSwitching.selectUser({ selectUserForDiscount: true })
    }
  }

  const showPromoCodeLink = !saleManager.hasAppliedPromoCode()
  const disabledDiscountMessage = getDisabledTooltipMessage(
    t,
    currentSale,
    TooltipContext.DISCOUNT
  )

  return (
    <>
      <Text type="signpost">
        <T _str="ADD" _context="Sale footer action row heading" />
      </Text>
      <Inline space={1}>
        {!displayDiscountRow && !currentSale.discount ? (
          <Tooltip
            className="wr-tooltip"
            direction="left"
            canBeShown={
              disabledDiscount && disabledDiscountMessage !== undefined
            }
            header={disabledDiscountMessage?.title ?? ''}
            message={disabledDiscountMessage?.message ?? ''}
            icon="info-circle"
          >
            <span
              className={classNames(
                'vd-text-label pro-sale-discount-link sale-total-header--link',
                {
                  'vd-link': !disabledDiscount,
                  'sale-link-disabled': disabledDiscount,
                }
              )}
              onClick={handleDiscountClick}
            >
              {disabledDiscount ? (
                <Icon icon="fa-lock" className="vd-mr2" />
              ) : null}{' '}
              <T _str="Discount" _context="Discount button link" />
            </span>
          </Tooltip>
        ) : null}
        {/* TODO: Tooltip with lock icon, just like the discount link */}
        {showPromoCodeLink && <PromoCode />}
        {showSaleNoteLink && (
          <Link
            className="vd-text-label pro-sale-note-link sale-total-header--link"
            href="#"
            onClick={onNoteLinkClick}
            data-track="sale-note-link"
          >
            <T st _str="Note" _context="Note link on sale footer" />
          </Link>
        )}
      </Inline>
    </>
  )
}
